import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  Launch,
  Warning,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  ExpandLess,
  SecurityOutlined,
  PriorityHigh,
  Security,
} from '@mui/icons-material';
import { TableSortLabel, Tooltip } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { setContractDetails, showDetailModal } from '../store/slices/fraudAnalysisSlice';
import { SearchResult, VendorResult, selectVendorResults } from '../store/slices/searchSlice';
import { fraudUtils } from '../services/api';
import FraudAnalysisSummary from './FraudAnalysisSummary';
import IrregularitySpecificCards from './IrregularitySpecificCards';
import VendorPatternCard from './fraud/VendorPatternCard';
import VendorDetailModal from './fraud/VendorDetailModal';
import DeepResearchModal from './DeepResearchModal';
import EnhancedLoadingState from './EnhancedLoadingState';
import FraudRiskBadge from './FraudRiskBadge';

type ViewMode = 'cards' | 'table';
type Order = 'asc' | 'desc';
type OrderBy = keyof SearchResult;

const DynamicResultsDisplay: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    results, 
    loading, 
    totalResults, 
    currentType, 
    explanation,
    investigationSummary,
    query,
    useRag
  } = useSelector((state: RootState) => state.search);
  
  // Determine if this is a standard search (hide risk indicators) or fraud detection search (show risk indicators)
  const isStandardSearch = !currentType || currentType === '';
  const shouldShowRiskIndicators = !isStandardSearch;
  
  const vendorResults = useSelector(selectVendorResults);

  // Default view: table for standard search, cards for fraud/irregularity searches
  const [viewMode, setViewMode] = useState<ViewMode>(isStandardSearch ? 'table' : 'cards');
  const [order, setOrder] = useState<Order>('asc');  // Changed to 'asc' so CRITICAL shows first

  // Update view mode when search type changes
  useEffect(() => {
    setViewMode(isStandardSearch ? 'table' : 'cards');
  }, [isStandardSearch]);
  const [orderBy, setOrderBy] = useState<OrderBy>('fraud_risk_level');
  const [aiAnalysisExpanded, setAiAnalysisExpanded] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorResult | null>(null);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [deepResearchModalOpen, setDeepResearchModalOpen] = useState(false);
  const [selectedContractForResearch, setSelectedContractForResearch] = useState<SearchResult | null>(null);

  const getRiskLevelOrder = (riskLevel: string | null | undefined) => {
    if (!riskLevel) return 999;
    switch (riskLevel.toUpperCase()) {
      case 'CRITICAL': return 1;
      case 'HIGH': return 2;
      case 'MEDIUM': return 3;
      case 'LOW': return 4;
      case 'LEGITIMATE': return 5;
      default: return 999;
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedResults = React.useMemo(() => {
    return [...results].sort((a, b) => {
      // Special handling for fraud_risk_level
      if (orderBy === 'fraud_risk_level') {
        const aRiskOrder = getRiskLevelOrder(a.fraud_risk_level);
        const bRiskOrder = getRiskLevelOrder(b.fraud_risk_level);
        const riskSort = order === 'asc' ? aRiskOrder - bRiskOrder : bRiskOrder - aRiskOrder;
        
        // If risk levels are the same, sort by amount as secondary criteria
        if (riskSort === 0) {
          const aAmount = a.amount || a.total_obligated_amount || 0;
          const bAmount = b.amount || b.total_obligated_amount || 0;
          return bAmount - aAmount; // Always highest amount first for secondary sort
        }
        
        return riskSort;
      }
      
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (aValue == null) return order === 'asc' ? -1 : 1;
      if (bValue == null) return order === 'asc' ? 1 : -1;
      
      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Fallback to string comparison
      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [results, order, orderBy]);

  const handleViewContract = (contract: SearchResult) => {
    dispatch(setContractDetails(contract));
    dispatch(showDetailModal());
  };

  const handleUSASpendingLink = (contract: SearchResult) => {
    // Use actual URL from API response, fallback to generated if none
    const url = contract.url || contract.usaspending_permalink || contract.sam_link || fraudUtils.generateUSASpendingLink(contract);
    if (url) window.open(url, '_blank');
  };

  const handleViewVendorDetails = (vendor: VendorResult) => {
    setSelectedVendor(vendor);
    setVendorModalOpen(true);
  };

  const handleCloseVendorModal = () => {
    setVendorModalOpen(false);
    setSelectedVendor(null);
  };

  const handleDeepResearch = (contract: SearchResult) => {
    setSelectedContractForResearch(contract);
    setDeepResearchModalOpen(true);
  };

  const handleCloseDeepResearchModal = () => {
    setDeepResearchModalOpen(false);
    setSelectedContractForResearch(null);
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel.toUpperCase()) {
      case 'CRITICAL':
        return <PriorityHigh sx={{ color: '#d32f2f', fontSize: '1.2rem' }} />;  // Red
      case 'HIGH':
        return <Error sx={{ color: '#f57c00', fontSize: '1.2rem' }} />;  // Orange
      case 'MEDIUM':
        return <Warning sx={{ color: '#fbc02d', fontSize: '1.2rem' }} />;  // Yellow
      case 'LOW':
        return <SecurityOutlined sx={{ color: '#4caf50', fontSize: '1.2rem' }} />;  // Green
      case 'LEGITIMATE':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: '1.2rem' }} />;  // Green
      default:
        return <Info sx={{ color: '#9e9e9e', fontSize: '1.2rem' }} />;
    }
  };

  if (loading) {
    return <EnhancedLoadingState useRag={useRag} searchType={currentType} query={query} />;
  }

  // Check if we have any results to display
  const hasResults = results.length > 0 || vendorResults.length > 0;

  // If no results AND no explanation, show nothing (initial state before any search)
  if (!hasResults && !explanation && !query) {
    return null;
  }

  // Determine if we have vendor-pattern results
  const hasVendorResults = vendorResults.length > 0;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 2 }}>
      <Box sx={{ py: 4 }}>
        {/* Investigation Summary for Fraud Searches */}
        {currentType && investigationSummary && (
          <FraudAnalysisSummary 
            summary={investigationSummary}
            irregularityType={currentType}
          />
        )}

        {/* AI Explanation */}
        {explanation && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                AI Analysis
              </Typography>
              <Button
                size="small"
                onClick={() => setAiAnalysisExpanded(!aiAnalysisExpanded)}
                endIcon={aiAnalysisExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ ml: 2 }}
              >
                {aiAnalysisExpanded ? 'Show Less' : 'Show More'}
              </Button>
            </Box>
            
            {/* Preview - show first part */}
            <Box sx={{ 
              position: 'relative',
              '& p': { mb: 1 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              '& strong': { fontWeight: 600 },
              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                fontWeight: 600, 
                mt: 1.5, 
                mb: 1,
                color: 'primary.main'
              }
            }}>
              {!aiAnalysisExpanded ? (
                <>
                  <Box sx={{ 
                    maxHeight: '150px', 
                    overflow: 'hidden',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '40px',
                      background: 'linear-gradient(to bottom, rgba(245, 245, 245, 0), rgba(245, 245, 245, 1))',
                      pointerEvents: 'none'
                    }
                  }}>
                    <ReactMarkdown>{explanation}</ReactMarkdown>
                  </Box>
                </>
              ) : (
                <ReactMarkdown>{explanation}</ReactMarkdown>
              )}
            </Box>
          </Paper>
        )}

        {/* Results Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h5">
            {totalResults > 0 ? `${totalResults} Results Found` : 'No Results Found'}
            {currentType && (
              <Chip
                label={currentType.replace('-', ' ').toUpperCase()}
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>

          {hasResults && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="cards">
                <ViewModule /> Cards
              </ToggleButton>
              <ToggleButton value="table">
                <ViewList /> Table
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* No Results Message */}
        {!hasResults && (
          <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Info sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No matching records found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search terms or filters. Check the AI Analysis above for suggestions.
            </Typography>
          </Paper>
        )}

        {/* Results Display - only show when there are results */}
        {hasResults && (viewMode === 'cards' ? (
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {hasVendorResults ? (
              // Display vendor pattern results using VendorPatternCard
              vendorResults.map((vendor, index) => (
                <Grid item xs={12} md={6} lg={4} key={vendor.vendor_name || index}>
                  <VendorPatternCard
                    vendor={vendor}
                    patternType={currentType}
                    onViewDetails={handleViewVendorDetails}
                  />
                </Grid>
              ))
            ) : (
              // Display individual contract results
              results.map((result, index) => (
                <Grid item xs={12} md={6} lg={4} key={result.id || index}>
                  {currentType ? (
                    <IrregularitySpecificCards 
                      contract={result}
                      irregularityType={currentType}
                      onViewDetails={() => handleViewContract(result)}
                      onViewUSASpending={() => handleUSASpendingLink(result)}
                      onDeepResearch={() => handleDeepResearch(result)}
                    />
                  ) : (
                    <StandardResultCard 
                      result={result}
                      onViewDetails={() => handleViewContract(result)}
                      onViewUSASpending={() => handleUSASpendingLink(result)}
                      onDeepResearch={() => handleDeepResearch(result)}
                      shouldShowRiskIndicators={shouldShowRiskIndicators}
                    />
                  )}
                </Grid>
              ))
            )}
          </Grid>
        ) : hasVendorResults ? (
          // Vendor Results Table View
          <TableContainer component={Paper} sx={{ maxHeight: '70vh', width: '100%', overflowY: 'auto' }}>
            <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '100px', padding: '8px' }}>Risk Level</TableCell>
                  <TableCell sx={{ width: '25%', padding: '8px' }}>Vendor Name</TableCell>
                  <TableCell align="right" sx={{ width: '12%', padding: '8px' }}>Total Value</TableCell>
                  <TableCell align="center" sx={{ width: '10%', padding: '8px' }}>Contracts</TableCell>
                  <TableCell sx={{ width: '20%', padding: '8px' }}>Pattern Type</TableCell>
                  <TableCell sx={{ width: '25%', padding: '8px' }}>Key Finding</TableCell>
                  <TableCell sx={{ width: '80px', padding: '8px', textAlign: 'center' }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorResults.map((vendor, index) => (
                  <TableRow
                    key={vendor.vendor_name || index}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewVendorDetails(vendor)}
                  >
                    <TableCell sx={{ padding: '8px' }}>
                      <FraudRiskBadge
                        riskLevel={
                          vendor.risk_score !== undefined
                            ? vendor.risk_score >= 75 ? 'CRITICAL'
                              : vendor.risk_score >= 50 ? 'HIGH'
                              : vendor.risk_score >= 25 ? 'MEDIUM'
                              : 'LOW'
                            : 'MEDIUM'
                        }
                        variant="chip"
                        size="small"
                        showIcon={true}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <Tooltip title={vendor.vendor_name || 'Unknown Vendor'} placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 500,
                            fontSize: '0.85rem'
                          }}
                        >
                          {vendor.vendor_name || 'Unknown Vendor'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '8px' }}>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                        {fraudUtils.formatCurrency(vendor.total_amount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: '8px' }}>
                      <Chip
                        label={vendor.contract_count || vendor.contracts?.length || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {currentType?.replace(/-/g, ' ').toUpperCase() || 'PATTERN'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <Tooltip title={`${vendor.contract_count} contracts, ${vendor.agencies?.length || 0} agencies`} placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}
                        >
                          {vendor.agencies?.slice(0, 2).join(', ') || 'Click for details'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewVendorDetails(vendor);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // Standard Contract Results Table View
          <TableContainer component={Paper} sx={{ maxHeight: '70vh', width: '100%', overflowY: 'auto' }}>
            <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  {shouldShowRiskIndicators && (
                    <TableCell sortDirection={orderBy === 'fraud_risk_level' ? order : false} sx={{ width: '120px', padding: '4px 8px' }}>
                      <TableSortLabel
                        active={orderBy === 'fraud_risk_level'}
                        direction={orderBy === 'fraud_risk_level' ? order : 'asc'}
                        onClick={() => handleRequestSort('fraud_risk_level')}
                      >
                        <Tooltip title="Fraud Risk Level - Click to sort">
                          <span>Risk Level</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell
                    sortDirection={orderBy === 'title' ? order : false}
                    sx={{ width: shouldShowRiskIndicators ? '24%' : '28%' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleRequestSort('title')}
                    >
                      Contract/Item
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === 'vendor_name' ? order : false}
                    sx={{ width: shouldShowRiskIndicators ? '19%' : '22%' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'vendor_name'}
                      direction={orderBy === 'vendor_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('vendor_name')}
                    >
                      Vendor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === 'amount' ? order : false}
                    align="right"
                    sx={{ width: '10%' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === 'agency' ? order : false}
                    sx={{ width: '15%' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'agency'}
                      direction={orderBy === 'agency' ? order : 'asc'}
                      onClick={() => handleRequestSort('agency')}
                    >
                      Agency
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: '40px', padding: '4px 4px', textAlign: 'center' }}>
                    <Tooltip title="Location (State)">
                      <span>St</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === 'date' ? order : false}
                    sx={{ width: '85px', padding: '4px 8px' }}
                  >
                    <TableSortLabel
                      active={orderBy === 'date'}
                      direction={orderBy === 'date' ? order : 'asc'}
                      onClick={() => handleRequestSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: '50px', padding: '4px 4px', textAlign: 'center' }}>Link</TableCell>
                  {shouldShowRiskIndicators && (
                    <TableCell sx={{ width: '60px', padding: '4px 4px', textAlign: 'center' }}>
                      <Tooltip title="Deep Research Analysis">
                        <span>Research</span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((result, index) => (
                  <TableRow 
                    key={result.id || index}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewContract(result)}
                  >
                    {shouldShowRiskIndicators && (
                      <TableCell sx={{ padding: '4px 8px' }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <FraudRiskBadge
                            riskLevel={result.fraud_risk_level}
                            riskMessage={result.fraud_risk_message}
                            riskFactors={result.fraud_risk_factors}
                            variant="chip"
                            size="small"
                            showIcon={true}
                            animated={result.fraud_risk_level === 'CRITICAL'}
                          />
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={{ padding: '8px' }}>
                      <Tooltip title={result.title || result.description || 'No description'} placement="top">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.3,
                            fontSize: '0.8rem'
                          }}
                        >
                          {result.title || result.description || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <Tooltip title={result.vendor || result.vendor_name || 'No vendor information'} placement="top">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.8rem'
                          }}
                        >
                          {result.vendor || result.vendor_name || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ padding: '8px' }}>
                      <Tooltip title={`Contract Value: ${fraudUtils.formatCurrency(result.amount || result.total_obligated_amount || 0)}`} placement="top">
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {fraudUtils.formatCurrency(result.amount || result.total_obligated_amount || 0)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '8px' }}>
                      <Tooltip title={result.agency || 'No agency information'} placement="top">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.8rem'
                          }}
                        >
                          {result.agency || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '2px 4px', textAlign: 'center' }}>
                      <Tooltip title={result.location ? `${result.location.city || ''}, ${result.location.state || ''}` : 'No location'} placement="top">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            lineHeight: 1
                          }}
                        >
                          {result.location?.state ? result.location.state.substring(0, 2).toUpperCase() : '--'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ padding: '4px 6px' }}>
                      <Typography variant="body2" noWrap sx={{ fontSize: '0.7rem' }}>
                        {result.date ? new Date(result.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: '4px' }} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip title="Open in USAspending">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = result.url || result.usaspending_permalink || result.sam_link;
                              if (url) window.open(url, '_blank');
                            }}
                            sx={{ padding: '4px' }}
                          >
                            <Launch fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {shouldShowRiskIndicators && (
                      <TableCell sx={{ padding: '4px' }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Tooltip title="Start Deep Research Analysis">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeepResearch(result);
                              }}
                              sx={{ 
                                padding: '4px',
                                color: 'primary.main',
                                '&:hover': {
                                  bgcolor: 'primary.50',
                                }
                              }}
                            >
                              <Security fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}

        {/* Vendor Detail Modal */}
        <VendorDetailModal
          vendor={selectedVendor}
          patternType={currentType}
          open={vendorModalOpen}
          onClose={handleCloseVendorModal}
        />

        {/* Deep Research Modal */}
        <DeepResearchModal
          open={deepResearchModalOpen}
          onClose={handleCloseDeepResearchModal}
          contract={selectedContractForResearch}
        />
      </Box>
    </Box>
  );
};

// Standard result card for non-irregularity searches
const StandardResultCard: React.FC<{
  result: SearchResult;
  onViewDetails: () => void;
  onViewUSASpending: () => void;
  onDeepResearch?: () => void;
  shouldShowRiskIndicators?: boolean;
}> = ({ result, onViewDetails, onViewUSASpending, onDeepResearch, shouldShowRiskIndicators = true }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {result.title || result.description}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Vendor:</strong> {result.vendor || result.vendor_name || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Amount:</strong> {fraudUtils.formatCurrency(result.amount || result.total_obligated_amount || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Agency:</strong> {result.agency || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Date:</strong> {fraudUtils.formatDate(result.date)}
          </Typography>
        </Box>

        {shouldShowRiskIndicators && result.fraud_risk_level && result.fraud_risk_level !== 'UNKNOWN' && (
          <Box sx={{ mb: 2 }}>
            <FraudRiskBadge
              riskLevel={result.fraud_risk_level}
              riskMessage={result.fraud_risk_message}
              riskFactors={result.fraud_risk_factors}
              variant="chip"
              size="medium"
              animated={result.fraud_risk_level === 'CRITICAL'}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
          <Button size="small" onClick={onViewDetails}>
            View Details
          </Button>
          {(result.url || result.usaspending_permalink || result.sam_link) && (
            <Button 
              size="small" 
              onClick={() => {
                const url = result.url || result.usaspending_permalink || result.sam_link;
                if (url) window.open(url, '_blank');
              }}
              endIcon={<Launch fontSize="small" />}
            >
              {result.sam_link ? 'SAM.gov' : 'USAspending'}
            </Button>
          )}
          {shouldShowRiskIndicators && onDeepResearch && (
            <Button 
              size="small" 
              onClick={onDeepResearch}
              startIcon={<Security fontSize="small" />}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.50',
                }
              }}
            >
              Deep Research
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DynamicResultsDisplay;